import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import { HelpCircle, UserPlus } from 'lucide-react';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { ensureCurrentUser } from '../../util/data';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { NamedLink, Page } from '../../components';
import useRevealOnReady from '../../hooks/useRevealOnReady';
import revealCss from '../../styles/contentReveal.module.css';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import classNames from 'classnames';

import {
  START_SELLING_FAQ_GROUPS,
  START_SELLING_PROCESS_STEPS,
  START_SELLING_TESTIMONIAL_COLUMNS,
} from '../StartSellingLoginPage/startSellingGuideSteps';
import css from './StartSellingSignupPage.module.css';
import buttonCss from '../LandingPage/components/HomepageButtons.module.css';

const SUPPORT_EMAIL = 'hello@unvarnished.clothing';

const renderFaqAnswer = answer => {
  if (!answer.includes(SUPPORT_EMAIL)) {
    return answer;
  }

  const parts = answer.split(SUPPORT_EMAIL);

  return parts.map((part, index) => (
    <React.Fragment key={`${part}-${index}`}>
      {part}
      {index < parts.length - 1 ? <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> : null}
    </React.Fragment>
  ));
};

export const StartSellingSignupPageComponent = props => {
  const { currentUser, isAuthenticated, scrollingDisabled } = props;
  const [topbarHeight, setTopbarHeight] = useState(null);
  const [openFaqItem, setOpenFaqItem] = useState('Logistics-0');

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'AuthenticationPage.schemaTitleSignup' },
    { marketplaceName }
  );
  const schemaDescription = intl.formatMessage(
    { id: 'AuthenticationPage.schemaDescriptionSignup' },
    { marketplaceName }
  );

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const defaultFrom = pathByRouteName('NewListingPage', routeConfiguration);
  const hasRevealedContent = useRevealOnReady(true);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const readTopbarHeight = () => {
      const nav = window.document.querySelector('nav');

      if (!nav) {
        return false;
      }

      const nextHeight = Math.ceil(nav.getBoundingClientRect().height);
      if (!nextHeight) {
        return false;
      }

      setTopbarHeight(previous => (previous === nextHeight ? previous : nextHeight));
      return true;
    };

    readTopbarHeight();
    const initialMeasureInterval = window.setInterval(() => {
      const measured = readTopbarHeight();
      if (measured) {
        window.clearInterval(initialMeasureInterval);
      }
    }, 150);

    window.addEventListener('resize', readTopbarHeight);

    const nav = window.document.querySelector('nav');
    let resizeObserver;

    if (nav && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(readTopbarHeight);
      resizeObserver.observe(nav);
    }

    return () => {
      window.clearInterval(initialMeasureInterval);
      window.removeEventListener('resize', readTopbarHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  if (isAuthenticated && currentUserLoaded) {
    return <Redirect to={defaultFrom} />;
  }

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <TopbarContainer />
      <section
        className={classNames(css.root, revealedContentClassName)}
        style={
          topbarHeight
            ? {
                '--startSellingTopbarHeight': `${topbarHeight}px`,
              }
            : undefined
        }
      >
        <section className={css.heroProcess}>
          <div className={css.outer}>
            <div className={css.heroContent}>
              <p className={css.heroEyebrow}>Sell With Us</p>
              <h1 className={css.heroTitle}>
                Your wardrobe <span className={css.keepTogether}>worked hard.</span>
                <br />{' '}
                <em>
                  Now let it <span className={css.keepTogetherAccent}>work for you.</span>
                </em>
              </h1>
              <p className={css.heroText}>80% payout. We handle the listings. No drama.</p>

              <div className={css.stepsGrid}>
                {START_SELLING_PROCESS_STEPS.map(step => (
                  <article className={css.stepCard} key={step.number}>
                    <span className={css.stepNumber}>{step.number}</span>
                    <h2 className={css.stepTitle}>{step.title}</h2>
                    <p className={css.stepBody}>{step.body}</p>
                  </article>
                ))}
              </div>

              <div className={css.inlineCta}>
                <h2 className={css.inlineCtaTitle}>Ready?</h2>
                <div className={css.inlineCtaActions}>
                  <NamedLink
                    name="SignupPage"
                    to={{ state: { from: defaultFrom } }}
                    className={`${css.primaryCta} ${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
                  >
                    <UserPlus
                      className={`${buttonCss.icon} ${css.ctaIcon}`}
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    <span>Create account &amp; list</span>
                  </NamedLink>
                  <a
                    className={`${css.secondaryCta} ${buttonCss.button} ${buttonCss.buttonSecondary} ${buttonCss.buttonLight}`}
                    href="mailto:hello@unvarnished.clothing"
                  >
                    <HelpCircle
                      className={`${buttonCss.icon} ${css.ctaIcon}`}
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    <span>Have a question first?</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={css.faqSection} id="start-selling-details">
          <div className={css.outer}>
            <p className={css.sectionEyebrow}>The Nitty Gritty</p>
            <h2 className={css.sectionTitle}>Everything you need to know.</h2>
            <div className={css.faqGrid}>
              {START_SELLING_FAQ_GROUPS.map(group => (
                <div className={css.faqGroup} key={group.label}>
                  <h3 className={css.faqGroupLabel}>{group.label}</h3>
                  {group.items.map((item, index) => {
                    const itemId = `${group.label}-${index}`;
                    const isOpen = openFaqItem === itemId;
                    return (
                      <article
                        className={classNames(css.faqItem, { [css.faqItemOpen]: isOpen })}
                        key={item.question}
                      >
                        <button
                          type="button"
                          className={css.faqQuestion}
                          aria-expanded={isOpen}
                          onClick={() => setOpenFaqItem(isOpen ? null : itemId)}
                        >
                          <div className={css.faqQuestionInner}>
                            <span>{item.question}</span>
                            <span className={css.faqIcon}>+</span>
                          </div>
                        </button>
                        <div className={css.faqAnswer}>{renderFaqAnswer(item.answer)}</div>
                      </article>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={css.testimonialsSection}>
          <div className={css.outer}>
            <p className={css.sectionEyebrow}>In Their Words</p>
            <h2 className={css.sectionTitle}>Don't take our word for it.</h2>
            <div className={css.testimonialColumns}>
              {START_SELLING_TESTIMONIAL_COLUMNS.map(column => (
                <div className={css.testimonialColumn} key={column.label}>
                  <h3 className={css.testimonialLabel}>{column.label}</h3>
                  <article className={css.featuredQuote}>
                    <blockquote>{column.featured.quote}</blockquote>
                    <div className={css.quoteAttribution}>
                      <strong>{column.featured.name}</strong>
                      <span>{column.featured.role}</span>
                      {column.featured.tag ? (
                        <span className={css.quoteTag}>{column.featured.tag}</span>
                      ) : null}
                    </div>
                  </article>
                  {column.quotes.map(quote => (
                    <article className={css.quoteItem} key={quote.quote}>
                      <p className={css.quoteText}>{quote.quote}</p>
                      <p className={css.quoteMeta}>
                        <strong>{quote.attribution}</strong>
                        {quote.detail ? <span> - {quote.detail}</span> : null}
                      </p>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>
      <FooterContainer />
    </Page>
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const { currentUser } = state.user;

  return {
    currentUser,
    isAuthenticated,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const StartSellingSignupPage = compose(connect(mapStateToProps))(StartSellingSignupPageComponent);

export default StartSellingSignupPage;
